import { useMemo } from "react";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { PlayerCardReview, PlayerCardReviewContent, PlayerCardServices, ReviewSourceSystem } from "@/services/bedrock";
import { useDebounced } from "@/utils/useDebounced";
import { REVIEW_MIN_SCORE_SETTINGS } from "./utils";
import { DUMMY_REVIEWS } from "@/containers/Coach/Reputation/ReviewWidget/components/reviewWidgetPreviewData";

export type ReviewWidgetPreviewParams = Pick<
  PlayerCardReview,
  "displayType" | "reviewId" | "minScore" | "sourceSystems" | "linkToReviewPage"
>;

export const toReviewWidgetPreviewParams = (owner: ReviewWidgetPreviewParams): ReviewWidgetPreviewParams => ({
  displayType: owner.displayType,
  reviewId: owner.displayType === "SPECIFIC_REVIEW" ? owner.reviewId : null,
  minScore: REVIEW_MIN_SCORE_SETTINGS[owner.displayType] ? owner.minScore : null,
  sourceSystems: owner.displayType === "FEATURED_REVIEWS" ? [...(owner.sourceSystems ?? [])].sort() : [],
  linkToReviewPage: owner.linkToReviewPage,
});

const TEMPLATE_PREVIEW_CONTENT: PlayerCardReviewContent = {
  reviews: DUMMY_REVIEWS.filter(review => review.approvedForSocial),
  summary: {
    star: { rating: 4.9, totalResponses: 42 },
    nps: { rating: 78, totalResponses: 31 },
    sentiment: { rating: 96, totalResponses: 42 },
  },
  reviewPageUrl: "#",
};

const templatePreviewContent = (params: ReviewWidgetPreviewParams): PlayerCardReviewContent | null => {
  const { reviews = [], summary = {} } = TEMPLATE_PREVIEW_CONTENT;
  const reviewPageUrl = params.linkToReviewPage ? TEMPLATE_PREVIEW_CONTENT.reviewPageUrl : undefined;
  switch (params.displayType) {
    case "FEATURED_REVIEWS": {
      const sources = params.sourceSystems;
      const featured = sources.length
        ? reviews.filter(review => sources.includes(review.sourceSystem as ReviewSourceSystem))
        : reviews;
      return featured.length ? { reviews: featured, reviewPageUrl } : null;
    }
    case "SPECIFIC_REVIEW":
      return { reviews: reviews.slice(0, 1), reviewPageUrl };
    case "STAR_RATING":
      return { summary: { star: summary.star }, reviewPageUrl };
    case "NPS_RATING":
      return { summary: { nps: summary.nps }, reviewPageUrl };
    case "SATISFACTION_RATING":
      return { summary: { sentiment: summary.sentiment }, reviewPageUrl };
  }
};

export interface ReviewWidgetPreview {
  available: boolean | undefined;
  content: PlayerCardReviewContent | null | undefined;
}

export const useReviewWidgetPreviews = (
  playerCardId: number | undefined,
  isTemplate: boolean,
  owners: ReviewWidgetPreviewParams[]
): ReviewWidgetPreview[] => {
  const paramsKey = JSON.stringify(owners.map(toReviewWidgetPreviewParams));
  const debouncedParamsKey = useDebounced(paramsKey, 400);
  const debouncedParams: ReviewWidgetPreviewParams[] = useMemo(
    () => JSON.parse(debouncedParamsKey),
    [debouncedParamsKey]
  );
  const canFetch = !isTemplate && !!playerCardId && playerCardId > 0;

  const data = useQueries({
    queries: debouncedParams.map(params => ({
      queryKey: ["playerCardReviewPreview", playerCardId, params],
      queryFn: () => PlayerCardServices.getReviewPreview(playerCardId!, params),
      enabled: canFetch,
      placeholderData: keepPreviousData,
      staleTime: 60_000,
    })),
    combine: results => results.map(result => result.data),
  });

  return useMemo(
    () =>
      debouncedParams.map((params, index) =>
        isTemplate
          ? { available: true, content: templatePreviewContent(params) }
          : { available: data[index]?.available, content: data[index]?.owner.content }
      ),
    [debouncedParams, data, isTemplate]
  );
};

export const useReviewWidgetPreview = (
  playerCardId: number | undefined,
  isTemplate: boolean,
  owner: ReviewWidgetPreviewParams
): ReviewWidgetPreview => useReviewWidgetPreviews(playerCardId, isTemplate, [owner])[0];
